import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";

import { KpiTile, PageHeader, ProvenanceBadge, StatusDot } from "@/components/comms";
import {
  type ShareAccountDetail,
  type ShareRange,
  tierHeldLabel,
  useShareAccount,
} from "@/lib/comms";
import { cn, formatSignedPercent } from "@/lib/utils";

import { DraftDrawer } from "./draft-drawer";
import { MixDonut } from "./mix-donut";
import { NudgeHistoryDrawer } from "./nudge-history-drawer";
import { TrendChart } from "./trend-chart";

const RANGES: readonly { value: ShareRange; label: string }[] = [
  { value: "1", label: "1Q" },
  { value: "2", label: "2Q" },
  { value: "3", label: "3Q" },
  { value: "4", label: "4Q" },
  { value: "1Y", label: "1Y" },
  { value: "5Y", label: "5Y" },
  { value: "MAX", label: "Max" },
];

/** Account ids are name slugs — "bright-eyes-optometry" → "Bright Eyes Optometry". */
function nameFromSlug(slug: string): string {
  return slug
    .split("-")
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(" ");
}

export function ShareDetailPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [range, setRange] = useState<ShareRange>("3");
  const [nudgeHistoryOpen, setNudgeHistoryOpen] = useState(false);
  // ?draft=1 is the single source of truth for the draft drawer — "Approve &
  // send" on a list row lands here with it set, and opening/closing writes it
  // back, so URL and UI can never drift.
  const draftOpen = searchParams.get("draft") === "1";
  const setDraftOpen = (open: boolean) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (open) {
          next.set("draft", "1");
        } else {
          next.delete("draft");
        }
        return next;
      },
      { replace: true },
    );
  };
  const { data, isError } = useShareAccount(accountId ?? null, range);

  // Keep the last payload so range switches don't blank the charts mid-fetch —
  // keyed by account so switching accounts never shows the previous one.
  const lastRef = useRef<{ id: string; data: ShareAccountDetail } | undefined>(undefined);
  useEffect(() => {
    if (data && accountId) {
      lastRef.current = { id: accountId, data };
    }
  }, [data, accountId]);
  const last = lastRef.current;
  const account = data ?? (last && last.id === accountId ? last.data : undefined);

  // Canonical name once loaded; slug-derived fallback only while fetching.
  const name = account?.name ?? nameFromSlug(accountId ?? "");

  return (
    <div>
      <PageHeader
        backTo="/share"
        backLabel="Back to preferred customer list"
        title={name}
        titleBadge={account && <ProvenanceBadge provenance={account.provenance} />}
      />

      {!account ? (
        <p className="text-wg-06">{isError ? "Account not found." : "Loading account…"}</p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <KpiTile label="Tier held" value={tierHeldLabel(account.kpis.tier_held)} />
            <KpiTile
              label="Total nudges sent"
              value={account.kpis.nudges_sent}
              sub="View sent emails & outcomes"
              onClick={() => setNudgeHistoryOpen(true)}
            />
            <KpiTile
              label="Avg. share difference after nudge"
              value={formatSignedPercent(account.kpis.avg_share_diff_after_nudge)}
              valueClassName={account.kpis.avg_share_diff_after_nudge < 0 ? "text-risk" : "text-ok"}
            />
          </div>

          <div className="mt-5 grid items-start gap-4 lg:grid-cols-[1.6fr_1fr]">
            <section className="border-wg-03 rounded-xl border bg-white p-5">
              <h3 className="font-heading text-base font-semibold">
                J&amp;J order share over time
              </h3>
              {/* Derived from the data, not the button label — ranges span
                    N quarters back PLUS the current one (mockup behavior). */}
              <p className="text-wg-06 text-[12.5px]">
                {account ? `${account.series.length} quarters shown` : " "}
              </p>
              <div className="mt-3 mb-2 flex flex-wrap items-center justify-between gap-3">
                <div className="text-wg-07 flex flex-wrap items-center gap-3 text-[11.5px]">
                  <span className="flex items-center gap-1.5">
                    <span className="bg-jj-red size-2.5 rounded-full" /> Nudge sent
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="border-jj-red size-2.5 rounded-full border-2 bg-white" /> No
                    nudge
                  </span>
                  {account.markers.some((m) => m.kind === "skipped") && (
                    <span className="flex items-center gap-1.5">
                      <span className="bg-wg-06 size-2.5 rounded-full" /> Nudge skipped
                    </span>
                  )}
                  {account.pending && (
                    <span className="flex items-center gap-1.5">
                      <span className="bg-jj-red size-2.5 animate-pulse rounded-full" /> Pending —
                      click to review
                    </span>
                  )}
                  <span className="flex items-center gap-1.5">
                    <span className="border-wg-05 w-4 border-t-2 border-dashed" /> Tier thresholds
                  </span>
                </div>
                <div className="flex gap-1">
                  {RANGES.map((r) => (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setRange(r.value)}
                      className={cn(
                        "cursor-pointer rounded-md px-2 py-1 text-xs font-semibold",
                        range === r.value ? "bg-wg-08 text-white" : "text-wg-07 hover:bg-wg-02",
                      )}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
              <TrendChart
                series={account.series}
                markers={account.markers}
                tierHeld={account.kpis.tier_held}
                targetTier={account.kpis.target_tier}
                onPendingClick={() => setDraftOpen(true)}
              />
              <p className="text-wg-06 mt-2 text-xs">
                J&amp;J share of {name}’s contact-lens orders. Dashed lines = the tier band it sits
                in — the tier it holds and the next one up (one line only once T3 is held).
              </p>
              {account.pending && (
                <button
                  type="button"
                  onClick={() => setDraftOpen(true)}
                  className="border-jj-red text-jj-red mt-3 inline-flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-semibold"
                >
                  <StatusDot status="risk" pulse className="size-2.5" />
                  Review pending share email
                </button>
              )}
            </section>

            <div className="space-y-4">
              <section className="border-wg-03 rounded-xl border bg-white p-5">
                <h3 className="font-heading text-base font-semibold">Customer order share</h3>
                <p className="text-wg-06 mb-4 text-[12.5px]">
                  Share of this account’s contact-lens orders, current quarter
                </p>
                <MixDonut mix={account.mix} />
              </section>

              <section className="border-wg-03 rounded-xl border bg-white p-5">
                <h3 className="font-heading text-base font-semibold">About this account</h3>
                <p className="text-wg-07 mt-2 text-sm leading-relaxed">{account.about}</p>
                <div className="border-wg-03 mt-4 border-t pt-3">
                  <div className="text-wg-07 text-[10.5px] font-semibold tracking-[0.13em] uppercase">
                    Account rep
                  </div>
                  <div className="text-wg-08 mt-1 text-sm font-medium">{account.rep}</div>
                </div>
              </section>
            </div>
          </div>
        </>
      )}

      {/* Keyed by account: switching accounts remounts the drawer, so its
            local sent/already-sent state can never leak across accounts. */}
      {accountId && (
        <>
          <DraftDrawer
            key={accountId}
            accountId={accountId}
            open={draftOpen}
            onClose={() => setDraftOpen(false)}
          />
          <NudgeHistoryDrawer
            accountId={accountId}
            accountName={name}
            open={nudgeHistoryOpen}
            onClose={() => setNudgeHistoryOpen(false)}
          />
        </>
      )}
    </div>
  );
}
